import { supabase } from '../lib/supabase';

export const notificationService = {
  // Get user notifications
  async getNotifications(userId, options = {}) {
    try {
      let query = supabase?.from('notification_queue')?.select(`
          *,
          task:ipm_tasks(
            id, title, description, status, priority, due_date
          )
        `)?.eq('user_id', userId)?.order('created_at', { ascending: false });

      if (options?.status) {
        query = query?.eq('status', options?.status);
      }

      if (options?.limit) {
        query = query?.limit(options?.limit);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching notifications:', error);
      throw error;
    }
  },

  // Mark notification as read
  async markAsRead(notificationId) {
    try {
      const { data, error } = await supabase?.from('notification_queue')?.update({
          read_at: new Date()?.toISOString(),
          status: 'read'
        })?.eq('id', notificationId)?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error marking notification as read:', error);
      throw error;
    }
  },

  // Mark all notifications as read for a user
  async markAllAsRead(userId) {
    try {
      const { data, error } = await supabase?.from('notification_queue')?.update({
          read_at: new Date()?.toISOString(),
          status: 'read'
        })?.eq('user_id', userId)?.eq('status', 'pending');

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      throw error;
    }
  },

  // Get notification counts
  async getNotificationCounts(userId) {
    try {
      const { data, error } = await supabase?.from('notification_queue')?.select('id, status, priority')?.eq('user_id', userId);

      if (error) throw error;

      const notifications = data || [];
      return {
        total: notifications?.length,
        unread: notifications?.filter(n => n?.status === 'pending')?.length,
        urgent: notifications?.filter(n => n?.priority === 'urgent')?.length,
        high: notifications?.filter(n => n?.priority === 'high')?.length
      };
    } catch (error) {
      console.error('Error fetching notification counts:', error);
      throw error;
    }
  },

  // Create a new notification (for testing/manual creation)
  async createNotification(notificationData) {
    try {
      const { data, error } = await supabase?.from('notification_queue')?.insert([{
          user_id: notificationData?.userId,
          title: notificationData?.title,
          message: notificationData?.message,
          notification_type: notificationData?.type || 'task_assigned',
          priority: notificationData?.priority || 'medium',
          task_id: notificationData?.taskId,
          metadata: notificationData?.metadata || {},
          scheduled_for: notificationData?.scheduledFor || new Date()?.toISOString()
        }])?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating notification:', error);
      throw error;
    }
  },

  // Delete a notification
  async deleteNotification(notificationId) {
    try {
      const { error } = await supabase?.from('notification_queue')?.delete()?.eq('id', notificationId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error deleting notification:', error);
      throw error;
    }
  },

  // Get recent activity feed
  async getActivityFeed(userId, limit = 10) {
    try {
      const { data, error } = await supabase?.from('notification_queue')?.select(`
          *,
          task:ipm_tasks(
            id, title, status, priority, due_date,
            assigned_user:user_profiles!ipm_tasks_assigned_to_fkey(
              full_name, avatar_url
            )
          )
        `)?.eq('user_id', userId)?.order('created_at', { ascending: false })?.limit(limit);

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching activity feed:', error);
      throw error;
    }
  },

  // Get urgent notifications
  async getUrgentNotifications(userId) {
    try {
      const { data, error } = await supabase
        ?.from('notification_queue')
        ?.select('*')
        ?.eq('user_id', userId)
        ?.eq('is_urgent', true)
        ?.eq('status', 'pending')
        ?.order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching urgent notifications:', error);
      throw error;
    }
  },

  // Get audit event notifications
  async getAuditNotifications(userId, options = {}) {
    try {
      let query = supabase
        ?.from('notification_queue')
        ?.select('*')
        ?.eq('user_id', userId)
        ?.eq('notification_type', 'audit_event')
        ?.order('created_at', { ascending: false });

      if (options?.limit) {
        query = query?.limit(options?.limit);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching audit notifications:', error);
      throw error;
    }
  },

  // Get compliance alert notifications
  async getComplianceNotifications(userId, options = {}) {
    try {
      let query = supabase
        ?.from('notification_queue')
        ?.select('*')
        ?.eq('user_id', userId)
        ?.in('notification_type', ['compliance_violation', 'compliance_alert'])
        ?.order('created_at', { ascending: false });

      if (options?.limit) {
        query = query?.limit(options?.limit);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching compliance notifications:', error);
      throw error;
    }
  },

  // Send email notification
  async sendEmailNotification(notificationId) {
    try {
      const { data: notification, error: fetchError } = await supabase
        ?.from('notification_queue')
        ?.select('*')
        ?.eq('id', notificationId)
        ?.single();

      if (fetchError) throw fetchError;

      // Get user's email preferences
      const { data: prefs, error: prefsError } = await supabase
        ?.from('user_notification_preferences')
        ?.select('enable_email_notifications, urgent_alert_email')
        ?.eq('user_id', notification?.user_id)
        ?.single();

      if (prefsError) throw prefsError;

      // Check if email notifications are enabled
      if (!prefs?.enable_email_notifications) {
        return { sent: false, reason: 'Email notifications disabled' };
      }

      // Check if urgent emails are enabled for urgent notifications
      if (notification?.is_urgent && !prefs?.urgent_alert_email) {
        return { sent: false, reason: 'Urgent email alerts disabled' };
      }

      // Get user email
      const { data: profile, error: profileError } = await supabase
        ?.from('user_profiles')
        ?.select('email, full_name')
        ?.eq('id', notification?.user_id)
        ?.single();

      if (profileError) throw profileError;

      // Call Resend edge function
      const response = await fetch(`${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/send-notification-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env?.VITE_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({
          to: profile?.email,
          name: profile?.full_name,
          title: notification?.title,
          message: notification?.message,
          type: notification?.notification_type,
          priority: notification?.priority,
          isUrgent: notification?.is_urgent
        })
      });

      if (!response?.ok) {
        throw new Error('Failed to send email');
      }

      // Update notification email status
      const { error: updateError } = await supabase
        ?.from('notification_queue')
        ?.update({
          email_sent_at: new Date()?.toISOString(),
          email_status: 'sent'
        })
        ?.eq('id', notificationId);

      if (updateError) throw updateError;

      return { sent: true };
    } catch (error) {
      console.error('Error sending email notification:', error);
      
      // Update notification with error
      await supabase
        ?.from('notification_queue')
        ?.update({
          email_status: 'failed',
          email_error: error?.message
        })
        ?.eq('id', notificationId);

      throw error;
    }
  },

  // Send email summary
  async sendEmailSummary(userId, frequency = 'daily') {
    try {
      // Get user preferences
      const { data: prefs, error: prefsError } = await supabase
        ?.from('user_notification_preferences')
        ?.select('enable_email_summaries, email_summary_frequency')
        ?.eq('user_id', userId)
        ?.single();

      if (prefsError) throw prefsError;

      if (!prefs?.enable_email_summaries || prefs?.email_summary_frequency !== frequency) {
        return { sent: false, reason: 'Email summaries disabled or frequency mismatch' };
      }

      // Calculate date range based on frequency
      const now = new Date();
      const startDate = new Date();
      if (frequency === 'daily') {
        startDate?.setDate(startDate?.getDate() - 1);
      } else if (frequency === 'weekly') {
        startDate?.setDate(startDate?.getDate() - 7);
      }

      // Get notifications for the period
      const { data: notifications, error: notificationsError } = await supabase
        ?.from('notification_queue')
        ?.select('*')
        ?.eq('user_id', userId)
        ?.gte('created_at', startDate?.toISOString())
        ?.order('created_at', { ascending: false });

      if (notificationsError) throw notificationsError;

      if (!notifications || notifications?.length === 0) {
        return { sent: false, reason: 'No notifications to summarize' };
      }

      // Get user email
      const { data: profile, error: profileError } = await supabase
        ?.from('user_profiles')
        ?.select('email, full_name')
        ?.eq('id', userId)
        ?.single();

      if (profileError) throw profileError;

      // Call Resend edge function for summary
      const response = await fetch(`${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/send-notification-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env?.VITE_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({
          to: profile?.email,
          name: profile?.full_name,
          type: 'email_summary',
          frequency: frequency,
          notifications: notifications,
          summary: {
            total: notifications?.length,
            urgent: notifications?.filter(n => n?.is_urgent)?.length,
            audit: notifications?.filter(n => n?.notification_type === 'audit_event')?.length,
            compliance: notifications?.filter(n => n?.notification_type === 'compliance_violation')?.length
          }
        })
      });

      if (!response?.ok) {
        throw new Error('Failed to send email summary');
      }

      return { sent: true, count: notifications?.length };
    } catch (error) {
      console.error('Error sending email summary:', error);
      throw error;
    }
  },

  // Send SMS for urgent notifications
  async sendUrgentSMS(notificationId) {
    try {
      const { data: notification, error: fetchError } = await supabase
        ?.from('notification_queue')
        ?.select('*')
        ?.eq('id', notificationId)
        ?.single();

      if (fetchError) throw fetchError;

      // Only send SMS for urgent notifications
      if (!notification?.is_urgent) {
        return { sent: false, reason: 'Not an urgent notification' };
      }

      // Get user's SMS preferences
      const { data: prefs, error: prefsError } = await supabase
        ?.from('user_notification_preferences')
        ?.select('enable_phone_notifications, urgent_alert_sms, phone_number')
        ?.eq('user_id', notification?.user_id)
        ?.single();

      if (prefsError) throw prefsError;

      // Check if SMS is enabled for urgent alerts
      if (!prefs?.enable_phone_notifications || !prefs?.urgent_alert_sms) {
        return { sent: false, reason: 'Urgent SMS alerts disabled' };
      }

      if (!prefs?.phone_number) {
        return { sent: false, reason: 'No phone number configured' };
      }

      // Call Twilio edge function
      const response = await fetch(`${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/send-twilio-sms`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env?.VITE_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({
          to: prefs?.phone_number,
          message: `URGENT: ${notification?.title}\n${notification?.message}`
        })
      });

      if (!response?.ok) {
        throw new Error('Failed to send SMS');
      }

      // Update notification status
      const { error: updateError } = await supabase
        ?.from('notification_queue')
        ?.update({
          sent_at: new Date()?.toISOString(),
          status: 'sent'
        })
        ?.eq('id', notificationId);

      if (updateError) throw updateError;

      return { sent: true };
    } catch (error) {
      console.error('Error sending urgent SMS:', error);
      throw error;
    }
  },

  // Get notification preferences
  async getNotificationPreferences(userId) {
    try {
      const { data, error } = await supabase
        ?.from('user_notification_preferences')
        ?.select('*')
        ?.eq('user_id', userId)
        ?.single();

      if (error) {
        // If preferences don't exist, return defaults
        if (error?.code === 'PGRST116') {
          return {
            enable_phone_notifications: true,
            enable_email_notifications: true,
            enable_email_summaries: true,
            email_summary_frequency: 'daily',
            enable_audit_alerts: true,
            enable_compliance_alerts: true,
            urgent_alert_email: true,
            urgent_alert_sms: true
          };
        }
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error fetching notification preferences:', error);
      throw error;
    }
  },

  // Update notification preferences
  async updateNotificationPreferences(userId, preferences) {
    try {
      const { data, error } = await supabase
        ?.from('user_notification_preferences')
        ?.upsert({
          user_id: userId,
          ...preferences,
          updated_at: new Date()?.toISOString()
        })
        ?.select()
        ?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating notification preferences:', error);
      throw error;
    }
  }
};